import { auth } from '../config/firebase';
import { Shop, ShopMembership, MembershipRole, RateLimitError } from '../types';

const SHOP_API_BASE_URL = process.env.REACT_APP_SHOP_API_BASE_URL || 'https://shop.dev.carouselshopai.com';

class ShopService {
  private async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await this.getAuthToken();
    const url = `${SHOP_API_BASE_URL}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      // CSAI-533: a member limit can now be breached from the membership-update path (activating an
      // inactive member, or editing roles across the CLIENT/team boundary), so this client has to
      // recognise a 429 the same way invitationService does. Without it the limit modal never
      // renders and the refusal shows as a generic failure.
      if (response.status === 429) {
        const errorData = await response.json();
        throw new RateLimitError({
          resourceType: errorData.resourceType || 'ACTIVE_MEMBERS',
          currentUsage: errorData.currentUsage || 0,
          maxAllowed: errorData.maxAllowed || 0,
          tierType: errorData.tierType || 'UNKNOWN',
          // The server sends this under `message` (GlobalExceptionHandler). `rateLimitMessage` is
          // accepted as a fallback only because other services read that key.
          message: errorData.message || errorData.rateLimitMessage || 'Rate limit exceeded',
        });
      }
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    if (response.status === 204) {
      return {} as T; // No content
    }

    return response.json();
  }

  // Profile Management
  async updateDisplayName(displayName: string): Promise<{ message: string; uid: string; email: string; displayName: string }> {
    return this.request('/api/v1/auth/profile', {
      method: 'PUT',
      body: JSON.stringify({ displayName }),
    });
  }

  async logoutAllDevices(): Promise<void> {
    await this.request('/api/v1/auth/logout?allDevices=true', {
      method: 'POST',
    });
  }

  // Shop Management
  async getUserShops(): Promise<Shop[]> {
    return this.request<Shop[]>('/api/v1/shops');
  }

  async getShopById(shopId: string): Promise<Shop> {
    return this.request<Shop>(`/api/v1/shops/${shopId}`);
  }

  async createShop(shopData: {
    name: string;
    type: 'PERSONAL' | 'BUSINESS';
  }): Promise<Shop> {
    return this.request<Shop>('/api/v1/shops', {
      method: 'POST',
      body: JSON.stringify(shopData),
    });
  }

  async updateShop(shopId: string, shopData: {
    name?: string;
    description?: string;
    businessInfo?: string;
    type?: 'PERSONAL' | 'BUSINESS' | 'BUSINESS_PLUS';
  }): Promise<Shop> {
    return this.request<Shop>(`/api/v1/shops/${shopId}`, {
      method: 'PUT',
      body: JSON.stringify(shopData),
    });
  }

  async deleteShop(shopId: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}`, {
      method: 'DELETE',
    });
  }

  // Shop Memberships
  async getUserMemberships(): Promise<ShopMembership[]> {
    return this.request<ShopMembership[]>('/api/v1/shops/memberships');
  }

  async getShopMemberships(shopId: string): Promise<ShopMembership[]> {
    return this.request<ShopMembership[]>(`/api/v1/shops/${shopId}/members`);
  }

  async updateShopMembership(
    shopId: string,
    membershipId: string,
    updates: {
      roles?: MembershipRole[];
      active?: boolean;
    }
  ): Promise<ShopMembership> {
    return this.request<ShopMembership>(`/api/v1/shops/${shopId}/members/${membershipId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async removeShopMember(shopId: string, membershipId: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/members/${membershipId}`, {
      method: 'DELETE',
    });
  }

  // User Shop Access
  async getUserShopMembership(shopId: string): Promise<ShopMembership | null> {
    try {
      const memberships = await this.getUserMemberships();
      return memberships.find(m => m.shopId === shopId && m.active) || null;
    } catch (error) {
      console.error('Failed to get user shop membership:', error);
      return null;
    }
  }

  async setDefaultShop(shopId: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/default`, {
      method: 'PUT',
    });
  }

  // Utility methods
  hasShopRole(membership: ShopMembership | null, role: MembershipRole): boolean {
    return membership?.roles.includes(role) || false;
  }

  canUserManageShop(membership: ShopMembership | null): boolean {
    if (!membership || !membership.active) return false;
    return membership.roles.some(role =>
      ['PLATFORM_ADMIN', 'OWNER', 'ADMIN'].includes(role)
    );
  }

  canUserViewPriceData(membership: ShopMembership | null): boolean {
    if (!membership || !membership.active) return false;
    return membership.roles.some(role =>
      ['PLATFORM_ADMIN', 'OWNER', 'ADMIN', 'MEMBER', 'VIEWER'].includes(role)
    );
  }

  canUserManageSlides(membership: ShopMembership | null): boolean {
    if (!membership || !membership.active) return false;
    return membership.roles.some(role =>
      ['PLATFORM_ADMIN', 'OWNER', 'ADMIN', 'MEMBER'].includes(role)
    );
  }

  getUserHighestRole(membership: ShopMembership | null): MembershipRole | null {
    if (!membership || !membership.active) return null;

    const roleHierarchy: MembershipRole[] = [
      'PLATFORM_ADMIN',
      'OWNER',
      'ADMIN',
      'MEMBER',
      'VIEWER',
      'CLIENT',
      'VENDOR'
    ];

    for (const role of roleHierarchy) {
      if (membership.roles.includes(role)) {
        return role;
      }
    }

    return null;
  }
}

export const shopService = new ShopService();